import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { entryAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Save, Calculator, AlertCircle, Loader2, FileText, Users, RefreshCw, ArrowRight } from 'lucide-react';

export default function MarksEntryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [blueprint, setBlueprint] = useState([]);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await entryAPI.getAll();
      const data = response.data || [];
      setEntries(data);
      
      if (data.length > 0) {
        loadEntry(data[0]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const loadEntry = (entry) => {
    setSelectedEntryId(entry.id);
    const bp = JSON.parse(entry.items || '[]');
    const stu = JSON.parse(entry.students || '[]');
    setBlueprint(bp);
    setStudents(stu);
    
    // Initialize marks from students data if available
    const initialMarks = {};
    stu.forEach(student => {
      if (student.marks) {
        initialMarks[student.id] = student.marks;
      } else {
        initialMarks[student.id] = {};
      }
    });
    setMarks(initialMarks);
  };

  const handleEntryChange = (entryId) => {
    const entry = entries.find(e => e.id === entryId);
    if (entry) {
      loadEntry(entry);
    }
  };

  const handleMarkChange = (studentId, questionId, value) => {
    const question = blueprint.find(q => q.id === questionId);
    const maxMarks = question?.marks || 10;
    
    let parsedValue = value;
    
    // Handle absent marker
    if (value.toUpperCase() === 'A') {
      parsedValue = 'A';
    } else if (value === '' || value === '-') {
      parsedValue = '';
    } else {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return;
      if (numValue < 0 || numValue > maxMarks) {
        toast.error(`Marks must be between 0 and ${maxMarks}`);
        return;
      }
      parsedValue = numValue;
    }
    
    setMarks(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [questionId]: parsedValue
      }
    }));
  };

  const getStudentTotal = (studentId) => {
    const studentMarks = marks[studentId] || {};
    return blueprint.reduce((total, q) => {
      const mark = studentMarks[q.id];
      if (typeof mark === 'number') {
        return total + mark;
      }
      return total;
    }, 0);
  };

  const getQuestionTotal = (questionId) => {
    return students.reduce((total, student) => {
      const mark = marks[student.id]?.[questionId];
      if (typeof mark === 'number') {
        return total + mark;
      }
      return total;
    }, 0);
  };

  const getQuestionPercentage = (questionId) => {
    const question = blueprint.find(q => q.id === questionId);
    const maxPossible = (question?.marks || 0) * students.length;
    if (maxPossible === 0) return 0;
    return ((getQuestionTotal(questionId) / maxPossible) * 100).toFixed(1);
  };

  const saveMarks = async () => {
    if (!selectedEntryId) return false;
    
    setSaving(true);
    try {
      // Merge marks into students
      const studentsWithMarks = students.map(student => ({
        ...student,
        marks: marks[student.id] || {}
      }));
      
      await entryAPI.update(selectedEntryId, {
        students: JSON.stringify(studentsWithMarks)
      });
      toast.success('Marks saved successfully!');
      return true;
    } catch (error) {
      toast.error('Failed to save marks');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAndPreview = async () => {
    const success = await saveMarks();
    if (success) {
      toast.success('Marks saved! View your report.');
      navigate('/dashboard/preview');
    }
  };

  const maxTotalMarks = blueprint.reduce((sum, q) => sum + (q.marks || 0), 0);

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  // No entries at all - need to create blueprint
  if (entries.length === 0) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-navy">Marks Entry</h1>
          <p className="text-slate-600 mt-1 text-sm md:text-base">Enter marks for each student</p>
        </div>
        <Card>
          <CardContent className="p-8 md:p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-700 mb-2">No Blueprint Found</h3>
            <p className="text-sm text-slate-500 mb-4">Create a blueprint first to enter marks</p>
            <Button onClick={() => navigate('/dashboard/blueprint')} className="bg-navy hover:bg-navy-dark">
              <FileText className="w-4 h-4 mr-2" />
              Create Blueprint
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Find entry with blueprint items
  const selectedEntry = entries.find(e => e.id === selectedEntryId);
  const hasBlueprint = blueprint.length > 0;
  const hasStudents = students.length > 0;

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-navy">Marks Entry</h1>
          <p className="text-slate-600 mt-1 text-sm md:text-base">Enter marks for each student</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={fetchEntries}
            size="sm"
            className="md:size-default"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button 
            onClick={saveMarks} 
            disabled={saving || !hasBlueprint || !hasStudents}
            variant="outline"
            data-testid="save-marks-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            <span className="hidden sm:inline">Save</span>
          </Button>
          <Button 
            onClick={saveAndPreview} 
            disabled={saving || !hasBlueprint || !hasStudents}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="save-preview-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            <span className="hidden sm:inline">Save & Preview</span>
            <span className="sm:hidden">Preview</span>
          </Button>
        </div>
      </div>

      {/* Entry selector */}
      <Card className="mb-4 md:mb-6">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <span className="text-sm font-medium whitespace-nowrap">Select Report:</span>
            <select 
              className="flex-1 px-3 py-2 border rounded-md text-sm"
              value={selectedEntryId || ''}
              onChange={(e) => handleEntryChange(e.target.value)}
              data-testid="select-entry-marks"
            >
              {entries.map(entry => {
                const bp = JSON.parse(entry.items || '[]');
                const stu = JSON.parse(entry.students || '[]');
                return (
                  <option key={entry.id} value={entry.id}>
                    Class {entry.class_name} - {entry.subject} ({entry.exam} {entry.year}) 
                    [{bp.length} Q, {stu.length} Students]
                  </option>
                );
              })}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Show appropriate message based on state */}
      {!hasBlueprint ? (
        <Card>
          <CardContent className="p-8 md:p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-700 mb-2">Blueprint is Empty</h3>
            <p className="text-sm text-slate-500 mb-4">
              The selected report has no questions. Add questions to the blueprint first.
            </p>
            <Button onClick={() => navigate('/dashboard/blueprint')} className="bg-navy hover:bg-navy-dark">
              <FileText className="w-4 h-4 mr-2" />
              Go to Blueprint Builder
            </Button>
          </CardContent>
        </Card>
      ) : !hasStudents ? (
        <Card>
          <CardContent className="p-8 md:p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-700 mb-2">No Students Added</h3>
            <p className="text-sm text-slate-500 mb-4">
              Add students to this report before entering marks.
            </p>
            <Button onClick={() => navigate('/dashboard/students')} className="bg-navy hover:bg-navy-dark">
              <Users className="w-4 h-4 mr-2" />
              Add Students
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-6">
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold text-navy">{students.length}</div>
                <div className="text-xs md:text-sm text-slate-500">Students</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold text-navy">{blueprint.length}</div>
                <div className="text-xs md:text-sm text-slate-500">Questions</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 md:p-4">
                <div className="text-lg md:text-2xl font-bold text-navy">{maxTotalMarks}</div>
                <div className="text-xs md:text-sm text-slate-500">Max Marks</div>
              </CardContent>
            </Card>
          </div>

          {/* Marks table */}
          <Card>
            <CardHeader className="p-3 md:p-6">
              <CardTitle className="text-base md:text-lg flex items-center gap-2">
                <Calculator className="w-4 h-4 md:w-5 md:h-5" />
                Marks Entry Table
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs md:text-sm">
                  <thead>
                    <tr className="bg-navy text-white">
                      <th className="sticky left-0 bg-navy px-2 md:px-3 py-2 text-left font-medium z-10 min-w-[40px]">Roll</th>
                      <th className="px-2 md:px-3 py-2 text-left font-medium min-w-[100px] md:min-w-[150px]">Name</th>
                      {blueprint.map(q => (
                        <th key={q.id} className="px-1 md:px-3 py-2 text-center font-medium min-w-[50px] md:min-w-[60px]">
                          <div className="text-xs">{q.qLabel}</div>
                          <div className="text-[10px] font-normal opacity-80">({q.marks})</div>
                        </th>
                      ))}
                      <th className="px-2 md:px-3 py-2 text-center font-medium bg-amber text-white min-w-[60px]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student, idx) => (
                      <tr key={student.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="sticky left-0 bg-inherit px-2 md:px-3 py-1 md:py-2 font-medium border-r text-xs">{student.roll}</td>
                        <td className="px-2 md:px-3 py-1 md:py-2 border-r truncate max-w-[100px] md:max-w-[150px] text-xs" title={student.name}>
                          {student.name}
                        </td>
                        {blueprint.map(q => (
                          <td key={q.id} className="px-0.5 md:px-1 py-0.5 md:py-1 text-center">
                            <Input
                              type="text"
                              className="w-10 md:w-14 h-7 md:h-8 text-center text-xs md:text-sm tabular-nums p-1"
                              value={marks[student.id]?.[q.id] ?? ''}
                              onChange={(e) => handleMarkChange(student.id, q.id, e.target.value)}
                              placeholder="-"
                              data-testid={`mark-${student.id}-${q.id}`}
                            />
                          </td>
                        ))}
                        <td className="px-2 md:px-3 py-1 md:py-2 text-center font-bold text-navy bg-amber/10 text-xs md:text-sm">
                          {getStudentTotal(student.id)}/{maxTotalMarks}
                        </td>
                      </tr>
                    ))}
                    {/* Summary row */}
                    <tr className="bg-slate-100 font-medium">
                      <td colSpan={2} className="sticky left-0 bg-slate-100 px-2 md:px-3 py-2 text-right text-xs">Q Total:</td>
                      {blueprint.map(q => (
                        <td key={q.id} className="px-1 md:px-3 py-2 text-center text-xs">
                          {getQuestionTotal(q.id)}
                        </td>
                      ))}
                      <td className="px-2 md:px-3 py-2 text-center bg-amber/20"></td>
                    </tr>
                    <tr className="bg-slate-100 font-medium">
                      <td colSpan={2} className="sticky left-0 bg-slate-100 px-2 md:px-3 py-2 text-right text-xs">Q %:</td>
                      {blueprint.map(q => (
                        <td key={q.id} className="px-1 md:px-3 py-2 text-center text-emerald-600 text-xs">
                          {getQuestionPercentage(q.id)}%
                        </td>
                      ))}
                      <td className="px-2 md:px-3 py-2 text-center bg-amber/20"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 text-xs md:text-sm text-slate-500">
            <p>Tip: Enter 'A' for absent students. Use Tab to navigate between cells.</p>
          </div>
        </>
      )}
    </div>
  );
}
